import LegalLayout, { LegalList, Section } from "@/components/LegalLayout";
import { PRIVACY_CONTACT_EMAIL } from "@/lib/legal";

export default function DataHandlingPage() {
  return (
    <LegalLayout eyebrow="Transparency" title="How Callsect handles a call" updated="22 July 2026">
      <Section title="Before upload">
        <p>
          The signed-in user selects an audio file and confirms that they are authorised to upload and process
          the recording under the current legal version. Callsect validates the extension, size and readable
          audio duration and enforces the applicable account quotas.
        </p>
      </Section>

      <Section title="During transcription">
        <ol className="list-decimal space-y-3 pl-5 marker:text-indigo-400">
          <li>The browser sends the recording over HTTPS to the Callsect backend hosted on Render.</li>
          <li>The backend writes it to temporary server storage for validation.</li>
          <li>The audio is uploaded to AssemblyAI through the configured EU API endpoint.</li>
          <li>AssemblyAI returns transcript text, detected language, speaker turns and timestamps.</li>
          <li>Callsect requests immediate deletion of the AssemblyAI transcript and its associated uploaded audio.</li>
          <li>The temporary Render-side file is deleted after the transcription attempt, including failure paths.</li>
        </ol>
        <p>
          If the AssemblyAI upload succeeds but no transcript identifier is created, or if the deletion request
          fails, immediate remote deletion cannot be confirmed and AssemblyAI&apos;s standard retention rules may apply.
        </p>
      </Section>

      <Section title="During analysis">
        <p>
          Callsect sends the transcript text and speaker-labelled turns to OpenAI to generate the requested
          dissection, coaching, CRM notes and relationship-memory updates. The original audio is not sent to
          OpenAI in the current implementation.
        </p>
      </Section>

      <Section title="What Callsect keeps">
        <LegalList>
          <li>Call metadata, transcript, speaker segments, duration and limited transcription metadata.</li>
          <li>CRM notes, findings, dissection, coaching and relationship history.</li>
          <li>Writing DNA data if the user chooses to create a profile.</li>
          <li>Account, legal-acceptance, quota and limited security records needed to run the private beta.</li>
        </LegalList>
        <p>The original audio recording is not saved in the Callsect database.</p>
      </Section>

      <Section title="User control">
        <p>
          A user can delete an individual call, export account data in JSON, or permanently delete the whole
          account and its user-scoped data. Deleting a call also updates or removes the related relationship
          memory. For questions or rights requests, contact{" "}
          <a className="text-indigo-300 hover:text-indigo-200" href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section title="Important limitations">
        <p>
          Callsect cannot determine whether the uploader has satisfied every recording-law, employment-law,
          confidentiality or customer-contract requirement. That responsibility remains with the uploader and
          their organisation. Provider logs, backups or exceptional failure paths may follow provider-configured
          retention periods as described in the Privacy Notice.
        </p>
      </Section>
    </LegalLayout>
  );
}
